import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SupplierReference } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/model/Reference';
/* eslint-disable max-len */
import { TFunction } from 'i18next';
import { useDebouncedCallback } from 'use-debounce';
import { getLastVehicleDetail, getSearchStatus } from 'domains/catalog/Catalog.store';
import { isVehicleFilteredIamSearchResult } from 'domains/catalog/Catalog.utils';
import { STANDARD_EXCHANGE_REFERENCE_PATTERN } from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/IAMReferenceCard';

export const getTranslatedDisplayName = (
  t: TFunction,
  referenceNumber?: string,
  referenceName?: string,
  designation?: string,
  supplierReferences?: SupplierReference[],
  supplier?: string,
): string => {
  const rawSupplierReferences = supplierReferences?.filter((ref) => ref.type === 'RAW');
  const rawLabels =
    rawSupplierReferences && rawSupplierReferences.length > 0
      ? rawSupplierReferences?.map((rawSupplierReference) => (rawSupplierReference ? rawSupplierReference?.id : ''))
      : [];
  const name = referenceName ?? designation;
  const translatedName = referenceNumber?.startsWith(STANDARD_EXCHANGE_REFERENCE_PATTERN)
    ? `${name} [${t('catalog.iam.standard_exchange', 'STANDARD EXCHANGE')}]`
    : name;
  return [translatedName, supplier, ...rawLabels].filter(Boolean).join(' / ');
};

export const getDisplayName = (
  referenceName?: string,
  designation?: string,
  supplierReferences?: SupplierReference[],
  supplier?: string,
): string => {
  const rawSupplierReferences = supplierReferences?.filter((ref) => ref.type === 'RAW');
  const rawLabels =
    rawSupplierReferences && rawSupplierReferences.length > 0
      ? rawSupplierReferences?.map((rawSupplierReference) => (rawSupplierReference ? rawSupplierReference?.id : ''))
      : [];
  const name = referenceName ?? designation;

  return [name, supplier, ...rawLabels].filter(Boolean).join(' / ');
};

export function useWindowWidth(debounceTime = 100): number {
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  const setNewWindowWidth = useDebouncedCallback(() => {
    setWindowWidth(window.innerWidth);
  }, debounceTime);

  useEffect(() => {
    window.addEventListener('resize', setNewWindowWidth);
    return () => {
      window.removeEventListener('resize', setNewWindowWidth);
    };
  }, [setNewWindowWidth]);

  return windowWidth;
}

export const useSearchResultLabel = (dataLength: number, label: string) => {
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const searchStatus = useSelector(getSearchStatus);

  const getCompatibleCarLabel = () => {
    const brand = vehicleDetail?.vehicleBrand;
    const model = vehicleDetail?.iamVehicle?.modelType;
    const version = vehicleDetail?.iamVehicle?.version;
    const identifiedBy = vehicleDetail?.vehicleIdentifiedBy ? `[${vehicleDetail?.vehicleIdentifiedBy}]` : undefined;
    return [brand, model, version, identifiedBy].join(' ');
  };

  const isVehicleFilteredIamResult = isVehicleFilteredIamSearchResult(searchStatus, vehicleDetail?.vehicleKey);

  if (isVehicleFilteredIamResult) {
    return (
      <Trans
        i18nKey="catalog.search.iam.compatible_result.title"
        tOptions={{ number: dataLength, keyword: label, vehicleLabel: getCompatibleCarLabel() }}
        components={{
          strong: <strong />,
          large: <strong />,
          normal: <span />,
          normal_bold: <strong />,
        }}
      >
        {
          '<strong>{{ number }}</strong> result(s) compatible with the <strong>identified vehicle, {{ vehicleLabel }},</strong> for your search: <strong>{{ keyword }}</strong>'
        }
      </Trans>
    );
  }

  return (
    <Trans
      i18nKey="catalog.search.iam.result.title"
      tOptions={{ number: dataLength, keyword: label }}
      components={{
        strong: <strong />,
        large: <strong />,
        normal: <span />,
        normal_bold: <strong />,
      }}
    >
      {'<large>{{ number }}</large> <normal>result for your search</normal> <normal_bold>{{ keyword }}</normal_bold>'}
    </Trans>
  );
};
